import type { ExtractedLeadV2 } from "../../../types/lead";
import { cardMutedTextClass, cardSurfaceClass, cardTitleTextClass, compactInputClass, salesIndigoSurfaceCardClass } from "@/components/common/surfaceClasses";
import type { LeadLike, MergeSuggestionLike } from "./LeadSummary";

type LeadSummaryHeaderProps = {
  currentLead: LeadLike;
  e: ExtractedLeadV2;
  timelineDisplay: string;
  leadStatusClass: string;
  leadStatusLabel: string;
  leadAgeDisplay: string;
  compactView: boolean;
  setCompactView: (v: boolean) => void;
  mergeSuggestion: MergeSuggestionLike;
  setMergeSuggestion: (v: MergeSuggestionLike) => void;
  mergeLeadIntoCurrent: (sourceId: string) => void;
  systemTags: string[];
  editableTags: string[];
  newTagInput: string;
  setNewTagInput: (v: string) => void;
  updateCurrentLeadField: (field: keyof ExtractedLeadV2, value: any) => void;
  addTag: () => void;
};

export function LeadSummaryHeader(props: LeadSummaryHeaderProps) {
  const {
    currentLead,
    e,
    timelineDisplay,
    leadStatusClass,
    leadStatusLabel,
    leadAgeDisplay,
    compactView,
    setCompactView,
    mergeSuggestion,
    setMergeSuggestion,
    mergeLeadIntoCurrent,
    systemTags,
    editableTags,
    newTagInput,
    setNewTagInput,
    updateCurrentLeadField,
    addTag,
  } = props;
  const interest = (e.interest_level || "").trim().toLowerCase();
  const showInterest = interest === "hot" || interest === "warm" || interest === "cold";

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-3">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-1">
            Lead summary{currentLead.isTest ? " (TEST)" : ""}
          </h2>
          <p className="text-base sm:text-lg text-gray-500">
            {currentLead.name && currentLead.name !== "New Customer"
              ? `Lead: ${currentLead.name}`
              : currentLead.isTest
              ? "This lead was created from a pasted transcript and will not be sent to your CRM."
              : ""}
          </p>

          <div className="mt-2 mb-1 flex flex-wrap gap-2 text-xs sm:text-sm">
            {showInterest && (
              <span
                className={
                  "inline-flex items-center px-2.5 py-1 rounded-full font-semibold " +
                  (interest === "hot"
                    ? "bg-red-100 text-red-800 border border-red-200"
                    : interest === "warm"
                    ? "bg-amber-100 text-amber-800 border border-amber-200"
                    : "bg-blue-100 text-blue-800 border border-blue-200")
                }
              >
                {interest === "hot" ? "Hot" : interest === "warm" ? "Warm" : "Cold"}
              </span>
            )}

            {timelineDisplay !== "—" && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-medium">
                {timelineDisplay.replace(/_/g, " ")}
              </span>
            )}

            {e.enunciated_budget && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium">
                Budget: {e.enunciated_budget}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:items-end gap-2 w-full sm:w-auto">
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <span className={leadStatusClass}>{leadStatusLabel}</span>
            <span className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              Age: {leadAgeDisplay}
            </span>
          </div>

          <div className={`inline-flex max-w-full overflow-x-auto rounded-full border text-xs sm:text-sm ${cardSurfaceClass}`}>
            <button
              type="button"
              onClick={() => setCompactView(true)}
              className={
                "shrink-0 px-3 py-1.5 rounded-full whitespace-nowrap " +
                (compactView ? `bg-white font-semibold dark:bg-zinc-800 ${cardTitleTextClass}` : `${cardMutedTextClass} hover:bg-gray-100 dark:hover:bg-zinc-800/60`)
              }
            >
              Compact view
            </button>
            <button
              type="button"
              onClick={() => setCompactView(false)}
              className={
                "shrink-0 px-3 py-1.5 rounded-full whitespace-nowrap " +
                (!compactView ? `bg-white font-semibold dark:bg-zinc-800 ${cardTitleTextClass}` : `${cardMutedTextClass} hover:bg-gray-100 dark:hover:bg-zinc-800/60`)
              }
            >
              Detailed view
            </button>
          </div>
        </div>
      </div>

      {mergeSuggestion && !currentLead.sent && !currentLead.isTest && (
        <div className={`mt-3 mb-1 rounded-2xl px-3 py-2 text-xs sm:text-sm ${salesIndigoSurfaceCardClass} text-indigo-900 dark:text-indigo-200`}>
          <div className="font-semibold mb-1">Possible duplicate lead</div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="m-0">{mergeSuggestion.reason} You can review and merge the leads now, or keep them separate.</p>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 text-xs sm:text-sm dark:bg-indigo-500 dark:hover:bg-indigo-600"
                onClick={() => {
                  mergeLeadIntoCurrent(mergeSuggestion.sourceId);
                  setMergeSuggestion(null);
                }}
              >
                Review & merge
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-full bg-white border border-indigo-200 text-indigo-800 hover:bg-indigo-100 text-xs sm:text-sm dark:bg-zinc-800 dark:border-indigo-700 dark:text-indigo-200 dark:hover:bg-indigo-950/50"
                onClick={() => setMergeSuggestion(null)}
              >
                Keep separate
              </button>
            </div>
          </div>
        </div>
      )}

      {!compactView && (systemTags.length > 0 || editableTags.length > 0) && (
        <div className="mt-4">
          <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">Tags</div>
          <div className="mb-3">
            <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">System tags (read-only)</div>
            <div className="flex flex-wrap gap-1">
              {systemTags.length > 0 ? (
                systemTags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[11px] border border-gray-200">
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-[11px] text-gray-400">None</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Custom tags (editable)</div>
            <div className="flex flex-wrap gap-1 mb-2">
              {editableTags.length > 0 ? (
                editableTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 text-[11px] border border-blue-200"
                    onClick={() => {
                      const filtered = editableTags.filter((t) => t !== tag);
                      updateCurrentLeadField("interest_tags", filtered.length ? filtered : null);
                    }}
                  >
                    <span>{tag}</span>
                    <span className="text-[10px]">&times;</span>
                  </button>
                ))
              ) : (
                <span className="text-[11px] text-gray-400">No custom tags yet.</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                className={`flex-1 rounded-full px-3 py-1.5 text-xs ${compactInputClass}`}
                placeholder="Add tag and press Enter"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
              <button type="button" className="text-xs px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700" onClick={addTag}>
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
